import {
  Box,
  Tabs,
  Tab,
  Grid,
  IconButton,
  Typography,
  ClickAwayListener,
  Portal,
  TablePagination,
  Skeleton,
  TextField,
  CircularProgress,
  Menu,
  MenuItem,
} from "@mui/material";
import React, { useEffect, useState, useCallback, useMemo, useRef } from "react";
import SideNavbar from "../../../components/sideNavbar";
import Header from "../../../components/header";
import NewLeadTable from "./NewLeadTable";
import axiosInstance from "../../../services/axiosInstance";
import apiEndPointsConfig from "../../../services/apiEndPoints";
import CloseIcon from "@mui/icons-material/Close";
import CalendarMonthIcon from "@mui/icons-material/CalendarMonth";
import Calendar from "react-calendar";
import "react-calendar/dist/Calendar.css";
import TimerIcon from "@mui/icons-material/Timer";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";

import LeadStatsWidget from "./LeadStatsWidget";
import { Alert } from "@mui/material";


const NewLead = () => {
  // Format a Date object to "YYYY-MM-DD" using LOCAL timezone (not UTC).
  // toISOString() shifts the date to UTC which causes June 4 IST → June 3 UTC.
  const formatLocalDate = (date) => {
    const y = date.getFullYear();
    const m = String(date.getMonth() + 1).padStart(2, '0');
    const d = String(date.getDate()).padStart(2, '0');
    return `${y}-${m}-${d}`;
  };
  const [loading, setLoading] = useState(true);
  const [leadData, setLeadData] = useState({
    allLeads: [],
    reachedLeads: [],
    qualifiedLeads: [],
  });


  const [tab, setTab] = useState("all");
  const [selectedDateRange, setSelectedDateRange] = useState({ start: null, end: null });
  const [calendarVisible, setCalendarVisible] = useState(false);
  const [slaAnchorEl, setSlaAnchorEl] = useState(null);
  const [slaTimeRange, setSlaTimeRange] = useState({ label: "07:30 - 22:00", start: 450, end: 1320 });

  const SLA_PRESETS = [
    { label: "07:30 - 22:00", start: 450, end: 1320 },
    { label: "24 Hours", start: 0, end: 1440 },
  ];

  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(100);
  const [totalLeads, setTotalLeads] = useState(0);
  const [searchTerm, setSearchTerm] = useState("");
  const [searchQuery, setSearchQuery] = useState("");
  const [loadingStatus, setLoadingStatus] = useState(false);
  const [stats, setStats] = useState({ all: 0, reached: 0, qualified: 0, slaOnTime: 0, slaLate: 0 });

  const abortControllerRef = useRef(null);

  // Debounce search
  useEffect(() => {
    const timer = setTimeout(() => {
      setSearchQuery(searchTerm);
      setPage(0);
    }, 500);
    return () => clearTimeout(timer);
  }, [searchTerm]);

  const getLeadsData = useCallback(async (isSilent = false) => {
    if (abortControllerRef.current) {
      abortControllerRef.current.abort();
    }
    abortControllerRef.current = new AbortController();

    try {
      if (!isSilent) setLoadingStatus(true);

      const params = {
        page: page + 1,
        limit: rowsPerPage,
        search: searchQuery,
        type: tab === "all" ? "allLeads" : tab === "reached" ? "reachedLeads" : "qualifiedLeads",
        startDate: selectedDateRange.start ? formatLocalDate(selectedDateRange.start) : undefined,
        endDate: selectedDateRange.end ? formatLocalDate(selectedDateRange.end) : undefined,
        slaStart: slaTimeRange.start,
        slaEnd: slaTimeRange.end,
      };

      const res = await axiosInstance.get(
        `${apiEndPointsConfig.getCampaignsStatus}/me`,
        { params, signal: abortControllerRef.current.signal }
      );

      if (res.data.success) {
        const { data, counts, pagination } = res.data;

        // Use the categorical list directly from backend
        const listKey = tab === "all" ? "allLeads" : tab === "reached" ? "reachedLeads" : "qualifiedLeads";

        setLeadData({
          allLeads: tab === "all" ? data.allLeads : [],
          reachedLeads: tab === "reached" ? data.reachedLeads : [],
          qualifiedLeads: tab === "qualified" ? data.qualifiedLeads : [],
          currentList: data[listKey] || []
        });

        setStats({
          all: counts?.allLeads || 0,
          reached: counts?.reachedLeads || 0,
          qualified: counts?.qualifiedLeads || 0,
          slaOnTime: counts?.slaOnTime || 0,
          slaLate: counts?.slaLate || 0
        });

        setTotalLeads(pagination?.total || 0);
      }
    } catch (error) {
      if (axiosInstance.isCancel(error)) return;
      console.error("Error fetching leads:", error);
    } finally {
      if (!isSilent) setLoadingStatus(false);
      setLoading(false);
    }
  }, [page, rowsPerPage, tab, searchQuery, selectedDateRange, slaTimeRange]);

  useEffect(() => {
    getLeadsData();
    const interval = setInterval(getLeadsData, 30000);
    return () => clearInterval(interval);
  }, [getLeadsData]);

  const paginatedData = leadData.currentList || [];

  const handleTabChange = (e, newValue) => {
    setTab(newValue);
    setPage(0);
  };

  return (
    <Box>
      <Box sx={{ ml: { xs: "0px", md: "280px" } }}>
        <SideNavbar />
        <Box sx={{ display: { xs: "none", md: "initial" } }}>
          <Header title={"Lead Status"} />
        </Box>

        <Box component="main" sx={{ flexGrow: 1, px: 3, pt: 2 }}>
          {loading ? (
            <LoadingSkeleton />
          ) : (
            <>
              {/* ✅ KPI Widget */}
              <LeadStatsWidget leadData={{
                allLeads: stats.all,
                reachedLeads: stats.reached,
                qualifiedLeads: stats.qualified,
                slaOnTime: stats.slaOnTime,
                slaLate: stats.slaLate
              }} loading={loading} />


              <Grid
                container
                alignItems="center"
                justifyContent="space-between"
                sx={{ mb: 2 }}
              >
                <Grid item xs={12} sm={6}>
                  <Tabs
                    value={tab}
                    onChange={handleTabChange}
                    TabIndicatorProps={{
                      style: { backgroundColor: "#6366f1", height: 3 },
                    }}
                    sx={{ "& .Mui-selected": { color: "#6366f1 !important" } }}
                  >
                    <Tab label={`New Leads`} value="all" />
                    <Tab label={`Reached `} value="reached" />
                    <Tab label={`Qualified `} value="qualified" />
                  </Tabs>
                </Grid>


                <Grid
                  item
                  xs={12}
                  sm={6}
                  sx={{
                    display: "flex",
                    justifyContent: "flex-end",
                    alignItems: "center",
                    gap: 1,
                    mt: { xs: 2, sm: 0 },
                  }}
                >
                  <TextField
                    placeholder="Search leads..."
                    size="small"
                    value={searchTerm}
                    onChange={(e) => setSearchTerm(e.target.value)}
                    sx={{
                      "& .MuiOutlinedInput-root": {
                        borderRadius: "20px",
                        bgcolor: "#fff",
                        fontSize: 13,
                      }
                    }}
                  />
                  {selectedDateRange.start && (
                    <Box
                      sx={{
                        display: "flex",
                        alignItems: "center",
                        px: 2,
                        py: 0.5,
                        bgcolor: "#f1f5ff",
                        borderRadius: 5,
                        color: "#6366f1",
                        fontWeight: 600,
                        fontSize: 13,
                      }}
                    >
                      {selectedDateRange.start.toLocaleDateString("en-GB")} -{" "}
                      {selectedDateRange.end?.toLocaleDateString("en-GB")}
                      <IconButton
                        size="small"
                        onClick={() => setSelectedDateRange({ start: null, end: null })}
                        sx={{ ml: 1 }}
                      >
                        <CloseIcon fontSize="inherit" />
                      </IconButton>
                    </Box>
                  )}

                  {/* ✅ SLA Range Selector */}
                  <Box
                    onClick={(e) => setSlaAnchorEl(e.currentTarget)}
                    sx={{
                      bgcolor: "#fff",
                      color: "#64748b",
                      border: "1px solid #e2e8f0",
                      px: 2,
                      py: 1,
                      borderRadius: "20px",
                      display: "flex",
                      alignItems: "center",
                      gap: 1,
                      cursor: "pointer",
                      "&:hover": { borderColor: "#6366f1", color: "#6366f1" },
                    }}
                  >
                    <TimerIcon sx={{ fontSize: 18 }} />
                    <Typography fontSize={13} fontWeight={600} sx={{ whiteSpace: "nowrap" }}>
                      SLA: {slaTimeRange.label}
                    </Typography>
                    <ExpandMoreIcon sx={{ fontSize: 18 }} />
                  </Box>

                  <Menu
                    anchorEl={slaAnchorEl}
                    open={Boolean(slaAnchorEl)}
                    onClose={() => setSlaAnchorEl(null)}
                    PaperProps={{
                      sx: {
                        borderRadius: "12px",
                        mt: 1,
                        boxShadow: "0 4px 20px rgba(0,0,0,0.1)",
                        border: "1px solid #f1f5f9",
                      }
                    }}
                  >
                    {SLA_PRESETS.map((preset) => (
                      <MenuItem
                        key={preset.label}
                        onClick={() => {
                          setSlaTimeRange(preset);
                          setSlaAnchorEl(null);
                          setPage(0);
                        }}
                        sx={{
                          fontSize: 13,
                          fontWeight: 500,
                          py: 1,
                          px: 2,
                          color: slaTimeRange.label === preset.label ? "#6366f1" : "#1e293b",
                          bgcolor: slaTimeRange.label === preset.label ? "#f5f3ff" : "transparent",
                          "&:hover": { bgcolor: "#f8fafc" }
                        }}
                      >
                        {preset.label}
                      </MenuItem>
                    ))}
                  </Menu>

                  <Box
                    onClick={() => setCalendarVisible(!calendarVisible)}
                    sx={{
                      bgcolor: "#6366f1",
                      color: "white",
                      px: 2,
                      py: 1,
                      borderRadius: "20px",
                      display: "flex",
                      alignItems: "center",
                      gap: 1,
                      cursor: "pointer",
                      "&:hover": { bgcolor: "#4f46e5" },
                    }}
                  >
                    <CalendarMonthIcon sx={{ fontSize: 18 }} />
                    <Typography fontSize={13} fontWeight={600} sx={{ whiteSpace: "nowrap" }}>
                      Filter Date
                    </Typography>
                  </Box>

                  {calendarVisible && (
                    <Portal>
                      <ClickAwayListener
                        onClickAway={() => setCalendarVisible(false)}
                      >
                        <Box
                          sx={{
                            position: "fixed",
                            top: "160px",
                            right: "40px",
                            zIndex: 1300,
                            boxShadow: 10,
                            borderRadius: 2,
                            bgcolor: "#fff",
                            overflow: "hidden",
                          }}
                        >
                          <Calendar
                            selectRange
                            onChange={(r) => {
                              if (r && r.length === 2) {
                                setSelectedDateRange({ start: r[0], end: r[1] });
                                setCalendarVisible(false);
                                setPage(0);
                              }
                            }}
                            value={selectedDateRange.start ? [selectedDateRange.start, selectedDateRange.end] : null}
                            maxDate={new Date()}
                          />
                        </Box>
                      </ClickAwayListener>
                    </Portal>
                  )}
                </Grid>
              </Grid>

              <Box
                sx={{
                  width: "100%",
                  borderRadius: 2,
                  border: "1px solid #e5e7eb",
                  bgcolor: "#fff",
                  overflow: "hidden",
                }}
              >
                <Box sx={{ position: "relative" }}>
                  {loadingStatus && (
                    <Box sx={{
                      position: "absolute", top: 0, left: 0, right: 0, bottom: 0,
                      display: "flex", alignItems: "center", justifyContent: "center",
                      bgcolor: "rgba(255, 255, 255, 0.6)", zIndex: 2, backdropFilter: "blur(1px)"
                    }}>
                      <CircularProgress size={40} thickness={4} sx={{ color: "#6366f1" }} />
                    </Box>
                  )}
                  <NewLeadTable
                    data={paginatedData}
                    getLeadsData={getLeadsData}
                    isReachedLeads={tab !== "all"}
                  />
                </Box>
              </Box>

              <TablePagination
                component="div"
                count={totalLeads}
                page={page}
                onPageChange={(e, p) => setPage(p)}
                rowsPerPage={rowsPerPage}
                onRowsPerPageChange={(e) => {
                  setRowsPerPage(parseInt(e.target.value, 10));
                  setPage(0);
                }}
              />
            </>
          )}
        </Box>
      </Box>
    </Box>
  );
};

// Simple skeleton component for cleaner code
const LoadingSkeleton = () => (
  <Box sx={{ mt: 2 }}>
    <Skeleton
      variant="rectangular"
      width="100%"
      height={100}
      sx={{ borderRadius: 2, mb: 3 }}
    />
    <Skeleton variant="text" width={200} height={40} />
    {Array.from({ length: 5 }).map((_, i) => (
      <Skeleton key={i} variant="text" height={60} sx={{ my: 1 }} />
    ))}
  </Box>
);

export default NewLead;
